import Component from 'vue-class-component';
import { Inject } from 'vue-property-decorator';
import AccountService from '@/account/account.service';
import HelperOffset from '@/shared/component/helper/offset.vue';
import AlertMixin from '@/shared/alert/alert.mixin';
import { mixins } from 'vue-class-component';
import SigninService from './signin.service';
import { REFRESH_TOKEN, TOKEN } from '@/constants';
import { utils } from '@/shared/utils';
import UserActivityService from '@/entities/user-activity/user-activity.service';
import { REGISTER_URL } from '@/constants';
import { required } from 'vuelidate/lib/validators';


const validations = {
  login: {
    required,
  },
  password: {
    required,
  }
};

@Component({
  validations,
  mixins: [AlertMixin],
  components: {
    HelperOffset,
  },
})
export default class Signin extends mixins(AlertMixin) {
  @Inject('accountService')
  private accountService: () => AccountService;

  @Inject('signinService')
  private signinService: () => SigninService;

  @Inject('userActivityService')
  private userActivityService: () => UserActivityService;

  public mode = null;
  public redirectURI = null;
  public authenticationError = null;
  public login = null;
  public password = null;
  public hidden = true;
  public loading = false;
  public autofilled = false;
  public rules = {
    required: (value) => !!value || 'Required.',
  };
  public sendTo = null;

  public signin =  {
    username: null,
    password: null
  };

  public checkAutoFill() {
    const userPromise = this.detectAutofill('#username');
    const passwordPromise = this.detectAutofill('#password');
    Promise.all([userPromise, passwordPromise])
    .then((results: [boolean, boolean]) => {
      const [result1, result2] = results;
      this.autofilled = result1 && result2;
    })
    .catch((error) => {
      console.error('Error:', error);
    });
  }

  /**
   * check whether the specified element has been autofilled based on the computed style's 'appearance' property
   * @param element
   */
  public detectAutofill(element: string) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(window.getComputedStyle(document.querySelector(element), null)
          .getPropertyValue('appearance') === 'menulist-button'
        );
      }, 600);
    });
  }

  mounted() {
    this.$nextTick(() => {
      this.checkAutoFill();
    });
  }

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (to.query.mode) {
        vm.storeParams(to.query);
      }
    });
  }

  created(): void {
    if (this.accountService().authenticated) {
      this.$router.push({
        name: 'ApplicationUser',
      });
    } else {
      utils.cleanStorage();
    }

    if (this.$route.query.sendto) {
      this.sendTo = this.$route.query.sendto;
    }

    const invalidSubs = ['localhost', 'dev', 'stg', 'fredo', 'www', 'localhost:9000', 'portal'];
    const subDomain = window.location.host.split('.')[0];
    if (!invalidSubs.includes(subDomain)) {
      this.$router.push({
        name: 'SSOLogin',
        query: { domain: subDomain },
      });
    }

    this.focusInput();
  }


  public focusInput() {
    this.autofilled = false;
  }

  public storeParams(params) {
    if (params.mode) {
      this.mode = params.mode;
    }

    if (params.redirectUrl) {
      this.redirectURI = decodeURI(params.redirectUrl);
    }
  }

  public handleAutofill() {
  }

  public get hasEmptyValues() {
    return this.autofilled ? false : utils.isNullOrEmpty(this.login) || utils.isNullOrEmpty(this.password);
  }

  public doLogin(): void {
    if (this.login == null || this.password == null) {
      return;
    }
    this.loading = true;
    const data = { username: this.login.trim(), password: this.password };
    this.signinService()
      .signin(data)
      .then((result) => {
        const bearerToken = result.id_token;
        const refresToken = result.refreshToken;
        if (bearerToken) {
          localStorage.setItem(TOKEN, bearerToken);
          const decodedToken = utils.decodeToken(bearerToken);
          if (decodedToken) {
            localStorage.setItem('exp', decodedToken['exp']);
          }
        }

        if (refresToken) {
          localStorage.setItem(REFRESH_TOKEN, refresToken);
        }
        this.authenticationError = false;
        if (this.mode && this.mode === 'internal-sso') {
          if (this.redirectURI) {
            let redirectUrl = this.redirectURI + '?accessToken=' + bearerToken;
            if (refresToken) {
              redirectUrl += '&rt=' + refresToken;
            }
            window.location.href = redirectUrl;
          }
        }

        this.accountService()
          .retrieveAccount()
          .then(() => {
            this.userActivityService().createEvent(
              this.$store.getters.account,
              this.$store.getters.currentLocation,
              'portal login',
              'loggedin'
            );

            if (this.sendTo === 'userdetail') {
              this.$router.push({
                name: 'ApplicationUserView',
                params: { id: this.$store.getters.account.applicationUserId.toString() },
              });
            } else {
              const redirectUrl = localStorage.getItem('requested-url');
              if (redirectUrl) {
                localStorage.removeItem('requested-url');
                this.$router.push({ path: redirectUrl });
              } else {
                if (this.accountService().isAuthor() || this.accountService().isOrgAdmin()) {
                  this.$router.push({
                    name: 'ProductVersionApps',
                  });
                } else if (this.accountService().isReseller()) {
                  this.$router.push({
                    name: 'Organization',
                  });
                } else {
                  this.$router.push({
                    name: 'ApplicationUser',
                  });
                }
              }
            }
            localStorage.setItem('imageUrl', this.$store.getters.account.imageUrl);
          });
      })
      .catch((error) => {
        let msg = 'The email and password combination you entered is incorrect.';
        if (error.response && error.response.data && error.response.data.detail) {
          msg = error.response.data.detail;
        }
        this.showAlert(msg);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public showAlert(msg) {
    // Use sweetalert2
    this.$swal.fire({
      icon: 'error',
      title: 'Sign In Error',
      confirmButtonColor: '#000C19',
      text: msg,
      footer: '<strong>Try Again</strong>',
    });
  }

  public forgot() {
    this.$router.push({
      name: 'ResetPasswordInit',
    });
  }

  public register() {
    window.open(REGISTER_URL, '_blank');
  }

  public openSSO() {
    this.$router.push({
      name: 'SSOLogin',
    });
  }

  public get year() {
    return utils.getYear();
  }

  public get isMobile() {
    return this.$vuetify.breakpoint.width < 961;
  }

  public loginErrors() {
    const errors = [];
    if (!this.$v.login.$dirty) {
      return errors;
    }
    !this.$v.login.required && errors.push('Required');

    return errors;
  }

  public passwordErrors() {
    const errors = [];
    if (!this.$v.password.$dirty) {
      return errors;
    }
    !this.$v.password.required && errors.push('Required');

    return errors;
  }


  public get pageHeight() {
    return this.$vuetify.breakpoint.height;
  }
}
